/**
 * The closing card every map ends with: the jacarandas on campus under a
 * soft gradient, and a sign-off. The classic roadmap ends the same way.
 */
import styled from 'styled-components';

/** Height of the closing card, in pixels. */
export const FINALE_HEIGHT = 200;

const SERIF = "'Source Serif 4', 'Iowan Old Style', Georgia, serif";

const Card = styled.section<{$top: number; $height: number; $inset: number}>`
  position: absolute;
  left: ${({$inset}) => $inset}px;
  right: ${({$inset}) => $inset}px;
  top: ${({$top}) => $top}px;
  height: ${({$height}) => $height}px;
  border-radius: 18px;
  overflow: hidden;
  background:
    linear-gradient(
      180deg,
      rgba(31, 26, 23, 0.05) 0%,
      rgba(31, 26, 23, 0.55) 100%
    ),
    url('/stanford/jacaranda.jpg') center / cover no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 22px 26px;
  color: #fff;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.16);

  h3 {
    margin: 0;
    font-family: ${SERIF};
    font-size: clamp(1.4rem, 5vw, 1.9rem);
    font-weight: 600;
    line-height: 1.1;
    color: #fff;
  }

  p {
    margin: 6px 0 0;
    font-size: 0.9rem;
    opacity: 0.9;
  }
`;

/** Props for {@link MapFinale}. */
export interface MapFinaleProps {
  /** Top edge on the canvas, in pixels. */
  top: number;
  /** Height in pixels; {@link FINALE_HEIGHT} unless a map needs otherwise. */
  height?: number;
  /** Horizontal inset from the canvas edges, in pixels. */
  inset: number;
  /** Heading. Defaults to "That's all for now." */
  title?: string;
  /** Line under the heading. Defaults to a note that more weeks will appear. */
  subtitle?: string;
}

/** The closing card at the bottom of a map. */
export function MapFinale({
  top,
  height = FINALE_HEIGHT,
  inset,
  title = "That's all for now.",
  subtitle = 'New weeks appear here as the course unfolds.',
}: MapFinaleProps) {
  return (
    <Card $top={top} $height={height} $inset={inset}>
      <h3>{title}</h3>
      {subtitle && <p>{subtitle}</p>}
    </Card>
  );
}
